import { useMutation, useQuery, useQueryClient } from 'react-query'
import { ChainId } from 'symbiosis-js-sdk'

import { MerkleDistributor__factory } from '~/contracts/types'
import { CONFIG } from '~/lib/config'
import { useWallet } from '~/lib/useWallet'

export interface ClaimParams {
	tokenAddress: string
	index: number
	merkleProofs: string[]
	amount: string
}

interface RewardToken {
	address: string
	chainId: ChainId
}

interface FetchRewardsParams {
	account: string
	claimableTokens: RewardToken[]
}

export interface Reward {
	address: string
	amount: string | null
	chainId: ChainId
	proofIndex: number
	proofs: string[]
	tokenAddress: string
}

export function useClaimRewardTokens(rewardTokens: RewardToken[]) {
	const { web3, account } = useWallet()

	const queryClient = useQueryClient()

	const fetchRewards = async ({
		account,
		claimableTokens,
	}: FetchRewardsParams): Promise<Reward[]> => {
		const results = await Promise.allSettled(
			claimableTokens.map(async (token) => {
				const url = new URL(
					'/vesis/api/v1/rewards',
					CONFIG.rewardService.url
				)
				url.searchParams.append('chainId', String(token.chainId))
				url.searchParams.append('address', account)
				url.searchParams.append('tokenAddress', token.address)

				const response = await fetch(url.toString(), {
					cache: 'no-store',
					headers: {
						'Cache-Control': 'no-cache, no-store, must-revalidate',
					},
				})

				if (!response.ok) {
					throw new Error(`Error: ${response.statusText}`)
				}

				return response.json()
			})
		)

		return results.map((result, index) => {
			if (result.status === 'rejected') {
				const token = claimableTokens[index]
				return {
					address: account,
					amount: null,
					chainId: token.chainId,
					proofIndex: 0,
					proofs: [],
					tokenAddress: token.address,
				} as Reward
			}
			return result.value
		})
	}

	const { data: rewards, isFetching } = useQuery(
		['sis-rewards', account],
		() => fetchRewards({ account, claimableTokens: rewardTokens }),
		{
			enabled: !!account,
			initialData: [],
			refetchOnWindowFocus: false,
		}
	)

	const { isLoading: isClaiming, mutate: claim } = useMutation(
		async ({ tokenAddress, index, merkleProofs, amount }: ClaimParams) => {
			const rewardToken = CONFIG.rewardTokens.find(
				(token) => token.address === tokenAddress
			)

			const merkleDistributorContract =
				MerkleDistributor__factory.connect(
					rewardToken.merkleDistributor,
					web3.getSigner(account)
				)

			const tx = await merkleDistributorContract.claim(
				index,
				account,
				amount,
				merkleProofs
			)

			await tx.wait()

			return {
				tokenAddress,
			}
		},
		{
			onSuccess({ tokenAddress }) {
				queryClient.setQueryData(
					['sis-rewards', account],
					(prevData: Reward[]) => {
						const claimedIndex = prevData.findIndex(
							(reward) => reward.tokenAddress === tokenAddress
						)

						if (claimedIndex !== -1) {
							const updatedData = [...prevData]

							updatedData[claimedIndex] = {
								...updatedData[claimedIndex],
								amount: '0',
							}

							return updatedData
						}

						return prevData
					}
				)
			},
		}
	)

	return {
		isFetching,
		rewards,
		claim,
		isClaiming,
	}
}
